// components/AdminPanel.js - Panneau Admin Complet pour Gestion des Demandes et Comptes
import React, { useState, useEffect } from 'react';
import { API_BASE, getCsrfToken } from '../config';
import { useTranslation } from '../i18n/LanguageContext';
import {
  Users, Clock, DollarSign, Calendar, CheckCircle, XCircle, 
  AlertCircle, RefreshCw, Search, Filter, Home, UserPlus,
  Mail, Building, Phone, MapPin, FileText, Eye, Ban, UserCheck, Shield
} from 'lucide-react';

const AdminPanel = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('inscriptions');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // États pour les demandes d'inscription
  const [registrationRequests, setRegistrationRequests] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [searchRegistrations, setSearchRegistrations] = useState('');

  // États pour les demandes de congés
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [searchLeaves, setSearchLeaves] = useState('');
  const [filterStatusLeaves, setFilterStatusLeaves] = useState('pending');

  // États pour les notes de frais
  const [expenseRequests, setExpenseRequests] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [searchExpenses, setSearchExpenses] = useState('');
  const [filterStatusExpenses, setFilterStatusExpenses] = useState('pending');

  // États pour la gestion des comptes
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchUsers, setSearchUsers] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    filterRegistrations();
  }, [searchRegistrations, registrationRequests]);

  useEffect(() => {
    filterLeaves();
  }, [searchLeaves, filterStatusLeaves, leaveRequests]);

  useEffect(() => {
    filterExpenses();
  }, [searchExpenses, filterStatusExpenses, expenseRequests]);

  useEffect(() => {
    filterUsers();
  }, [searchUsers, allUsers]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadRegistrationRequests(),
        loadLeaveRequests(),
        loadExpenseRequests(),
        loadAllUsers()
      ]);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      setMessage(t('admin.error_loading'));
    } finally {
      setLoading(false);
    }
  };

  // Charger les demandes d'inscription
  const loadRegistrationRequests = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/pending-requests`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setRegistrationRequests(data.requests || []);
        setFilteredRegistrations(data.requests || []);
      }
    } catch (error) {
      console.error('Erreur chargement demandes inscription:', error);
    }
  };

  // Charger les demandes de congés
  const loadLeaveRequests = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/leaves/admin/all-requests`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // La route retourne 'requests' ou directement un tableau
        const requests = data.requests || data || [];
        setLeaveRequests(requests);
        setFilteredLeaves(requests);
      }
    } catch (error) {
      console.error('Erreur chargement demandes congés:', error);
    }
  };

  // Charger les notes de frais en attente
  const loadExpenseRequests = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/notes-frais/pending-validation`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // La route peut retourner 'expenses' ou directement un tableau
        const expenses = data.expenses || data || [];
        setExpenseRequests(expenses);
        setFilteredExpenses(expenses);
      }
    } catch (error) {
      console.error('Erreur chargement notes de frais:', error);
    }
  };

  // Charger tous les utilisateurs
  const loadAllUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/users`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.users || []);
        setFilteredUsers(data.users || []);
      }
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
    }
  };

  // Approuver une demande d'inscription
  const approveRegistration = async (requestId) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/approve-request/${requestId}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setMessage(t('admin.registration_approved'));
        await loadRegistrationRequests();
      } else {
        const data = await response.json();
        setMessage(`${t('admin.error_approval')}: ${data.error || t('admin.error_approval')}`);
      }
    } catch (error) {
      setMessage(t('admin.error_approval'));
    }
  };

  // Rejeter une demande d'inscription
  const rejectRegistration = async (requestId) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/reject-request/${requestId}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setMessage(t('admin.registration_rejected'));
        await loadRegistrationRequests();
      } else {
        const data = await response.json();
        setMessage(`${t('admin.error_rejection')}: ${data.error || t('admin.error_rejection')}`);
      }
    } catch (error) {
      setMessage(t('admin.error_rejection'));
    }
  };

  // Approuver une demande de congé
  const approveLeave = async (requestId) => {
    try {
      const csrf = await getCsrfToken();
      if (!csrf) {
        setMessage(t('admin.error_approval') || 'Please refresh the page and try again.');
        return;
      }
      const response = await fetch(`${API_BASE}/api/leaves/admin/approve/${requestId}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf }
      });
      if (response.ok) {
        setMessage(t('admin.leave_approved'));
        await loadLeaveRequests();
      }
    } catch (error) {
      setMessage(t('admin.error_approval'));
    }
  };

  // Rejeter une demande de congé
  const rejectLeave = async (requestId) => {
    try {
      const csrf = await getCsrfToken();
      if (!csrf) {
        setMessage(t('admin.error_rejection') || 'Please refresh the page and try again.');
        return;
      }
      const response = await fetch(`${API_BASE}/api/leaves/admin/reject/${requestId}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf },
        body: JSON.stringify({ reason: 'Rejeté par l\'administrateur' })
      });
      if (response.ok) {
        setMessage(t('admin.leave_rejected'));
        await loadLeaveRequests();
      }
    } catch (error) {
      setMessage(t('admin.error_rejection'));
    }
  };

  // Approuver une note de frais
  const approveExpense = async (expenseId) => {
    try {
      const csrf = await getCsrfToken();
      if (!csrf) {
        setMessage(t('admin.error_approval') || 'Please refresh the page and try again.');
        return;
      }
      const response = await fetch(`${API_BASE}/api/notes-frais/approve/${expenseId}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf }
      });
      if (response.ok) {
        setMessage(t('admin.expense_approved'));
        await loadExpenseRequests();
      }
    } catch (error) {
      setMessage(t('admin.error_approval'));
    }
  };

  // Rejeter une note de frais
  const rejectExpense = async (expenseId) => {
    try {
      const csrf = await getCsrfToken();
      if (!csrf) {
        setMessage(t('admin.error_rejection') || 'Please refresh the page and try again.');
        return;
      }
      const response = await fetch(`${API_BASE}/api/notes-frais/reject/${expenseId}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf },
        body: JSON.stringify({ reason: 'Rejeté par l\'administrateur' })
      });
      if (response.ok) {
        setMessage(t('admin.expense_rejected'));
        await loadExpenseRequests();
      }
    } catch (error) {
      setMessage(t('admin.error_rejection'));
    }
  };

  // Activer/Désactiver un utilisateur
  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/users/${userId}/toggle-status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: !currentStatus })
      });
      if (response.ok) {
        setMessage(!currentStatus ? t('admin.user_activated') : t('admin.user_deactivated'));
        await loadAllUsers();
      }
    } catch (error) {
      setMessage(t('admin.error_modification'));
    }
  };

  // Filtrer les demandes d'inscription
  const filterRegistrations = () => {
    let filtered = registrationRequests;
    if (searchRegistrations) {
      filtered = filtered.filter(req => 
        req.company_name?.toLowerCase().includes(searchRegistrations.toLowerCase()) ||
        req.admin_email?.toLowerCase().includes(searchRegistrations.toLowerCase())
      );
    }
    setFilteredRegistrations(filtered);
  };

  // Filtrer les demandes de congés
  const filterLeaves = () => {
    let filtered = leaveRequests;
    if (filterStatusLeaves !== 'all') {
      filtered = filtered.filter(req => req.status === filterStatusLeaves);
    }
    if (searchLeaves) {
      filtered = filtered.filter(req => 
        req.employee_name?.toLowerCase().includes(searchLeaves.toLowerCase()) ||
        req.leave_type?.toLowerCase().includes(searchLeaves.toLowerCase())
      );
    }
    setFilteredLeaves(filtered);
  };

  // Filtrer les notes de frais
  const filterExpenses = () => {
    let filtered = expenseRequests;
    if (filterStatusExpenses !== 'all') {
      filtered = filtered.filter(req => req.status === filterStatusExpenses);
    }
    if (searchExpenses) {
      filtered = filtered.filter(req => 
        req.employee_name?.toLowerCase().includes(searchExpenses.toLowerCase()) ||
        req.title?.toLowerCase().includes(searchExpenses.toLowerCase())
      );
    }
    setFilteredExpenses(filtered);
  };

  // Filtrer les utilisateurs
  const filterUsers = () => {
    let filtered = allUsers;
    if (searchUsers) {
      filtered = filtered.filter(user => 
        user.email?.toLowerCase().includes(searchUsers.toLowerCase()) ||
        user.first_name?.toLowerCase().includes(searchUsers.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchUsers.toLowerCase())
      );
    }
    setFilteredUsers(filtered);
  };

  const tabs = [
    { id: 'inscriptions', label: t('admin.tab_registrations'), icon: <UserPlus size={20} />, count: registrationRequests.length },
    { id: 'conges', label: t('admin.tab_leaves'), icon: <Calendar size={20} />, count: leaveRequests.filter(r => r.status === 'pending').length },
    { id: 'frais', label: t('admin.tab_expenses'), icon: <DollarSign size={20} />, count: expenseRequests.filter(r => r.status === 'pending').length },
    { id: 'comptes', label: t('admin.tab_accounts'), icon: <Users size={20} />, count: allUsers.length }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      paddingTop: '80px'
    }}>
      {/* Header */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '20px 40px',
        background: 'rgba(10, 14, 39, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: '20px'
      }}>
        <button
          onClick={() => onNavigate && onNavigate('home')}
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#ffffff',
            padding: '10px 16px',
            borderRadius: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          <Home size={18} />
          {t('common.back_to_home')}
        </button>
        <button
          onClick={() => onNavigate && onNavigate('admin-dashboard')}
          style={{
            background: 'linear-gradient(135deg, #00d4ff, #00a8cc)',
            border: 'none',
            color: '#ffffff',
            padding: '10px 16px',
            borderRadius: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 600,
            transition: 'all 0.3s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 212, 255, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <Shield size={18} />
          {t('admin.dashboard_admin')}
        </button>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>{t('admin.panel_title')}</h1>
        <button
          onClick={loadAllData}
          style={{
            background: 'linear-gradient(135deg, #00d4ff, #00a8cc)',
            border: 'none',
            color: '#ffffff',
            padding: '10px 16px',
            borderRadius: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          <RefreshCw size={18} />
          {t('admin.refresh')}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          position: 'fixed',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: message.includes('Erreur') ? 'rgba(239, 68, 68, 0.9)' : 'rgba(16, 185, 129, 0.9)',
          color: '#ffffff',
          padding: '12px 24px',
          borderRadius: '12px',
          zIndex: 999,
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
          {message}
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '12px',
        padding: '20px 40px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        overflowX: 'auto'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: activeTab === tab.id 
                ? 'linear-gradient(135deg, #00d4ff, #00a8cc)' 
                : 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              padding: '12px 20px',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }
            }}
          >
            {tab.icon}
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                background: 'rgba(239, 68, 68, 0.8)',
                color: '#ffffff',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: 700,
                marginLeft: '4px'
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '40px', maxWidth: '1600px', margin: '0 auto' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255, 255, 255, 0.7)' }}>
            {t('admin.loading')}
          </div>
        )}

        {/* Onglet Demandes d'Inscription */}
        {activeTab === 'inscriptions' && !loading && (
          <div>
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '24px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{
                flex: 1,
                minWidth: '300px',
                position: 'relative'
              }}>
                <Search size={20} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }} />
                <input
                  type="text"
                  placeholder={t('admin.search_registrations')}
                  value={searchRegistrations}
                  onChange={(e) => setSearchRegistrations(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {filteredRegistrations.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '20px',
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                {t('admin.no_registrations')}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '20px'
              }}>
                {filteredRegistrations.map(request => (
                  <div
                    key={request.id}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '20px',
                      padding: '24px',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px',
                      flexWrap: 'wrap',
                      gap: '16px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: 700,
                          marginBottom: '8px',
                          color: '#ffffff'
                        }}>
                          {request.company_name}
                        </h3>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                          gap: '12px',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '14px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Mail size={16} />
                            {request.admin_email}
                          </div>
                          {request.phone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Phone size={16} />
                              {request.phone}
                            </div>
                          )}
                          {request.address && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <MapPin size={16} />
                              {request.address}
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Calendar size={16} />
                            {new Date(request.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          onClick={() => approveRegistration(request.id)}
                          style={{
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            border: 'none',
                            color: '#ffffff',
                            padding: '10px 20px',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: 600,
                            transition: 'all 0.3s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <CheckCircle size={18} />
                          {t('admin.approve')}
                        </button>
                        <button
                          onClick={() => rejectRegistration(request.id)}
                          style={{
                            background: 'rgba(239, 68, 68, 0.8)',
                            border: 'none',
                            color: '#ffffff',
                            padding: '10px 20px',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: 600,
                            transition: 'all 0.3s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <XCircle size={18} />
                          {t('admin.reject')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Onglet Demandes de Congés */}
        {activeTab === 'conges' && !loading && (
          <div>
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '24px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{
                flex: 1,
                minWidth: '300px',
                position: 'relative'
              }}>
                <Search size={20} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }} />
                <input
                  type="text"
                  placeholder={t('admin.search_leaves')}
                  value={searchLeaves}
                  onChange={(e) => setSearchLeaves(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <select
                value={filterStatusLeaves}
                onChange={(e) => setFilterStatusLeaves(e.target.value)}
                style={{
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="all">{t('admin.all_statuses')}</option>
                <option value="pending">{t('admin.status_pending')}</option>
                <option value="approved">{t('admin.status_approved')}</option>
                <option value="rejected">{t('admin.status_rejected')}</option>
              </select>
            </div>

            {filteredLeaves.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '20px',
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                {t('admin.no_leaves')}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '20px'
              }}>
                {filteredLeaves.map(request => (
                  <div
                    key={request.id}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '20px',
                      padding: '24px'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: '16px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '12px'
                        }}>
                          <h3 style={{
                            fontSize: '18px',
                            fontWeight: 700,
                            margin: 0,
                            color: '#ffffff'
                          }}>
                            {request.employee_name}
                          </h3>
                          <span style={{
                            background: request.status === 'pending' 
                              ? 'rgba(255, 193, 7, 0.2)' 
                              : request.status === 'approved'
                              ? 'rgba(16, 185, 129, 0.2)'
                              : 'rgba(239, 68, 68, 0.2)',
                            color: request.status === 'pending'
                              ? '#ffc107'
                              : request.status === 'approved'
                              ? '#10b981'
                              : '#ef4444',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                          }}>
                            {request.status === 'pending' ? t('admin.status_pending') : request.status === 'approved' ? t('admin.status_approved') : t('admin.status_rejected')}
                          </span>
                        </div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '12px',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '14px'
                        }}>
                          <div><strong>{t('admin.type')}</strong> {request.leave_type}</div>
                          <div><strong>{t('admin.from')}</strong> {new Date(request.start_date).toLocaleDateString('fr-FR')}</div>
                          <div><strong>{t('admin.to')}</strong> {new Date(request.end_date).toLocaleDateString('fr-FR')}</div>
                          <div><strong>{t('admin.duration')}</strong> {request.days_requested} {t('admin.days')}</div>
                          {request.reason && (
                            <div style={{ gridColumn: '1 / -1' }}>
                              <strong>{t('admin.reason')}</strong> {request.reason}
                            </div>
                          )}
                        </div>
                      </div>
                      {request.status === 'pending' && (
                        <div style={{
                          display: 'flex',
                          gap: '12px',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={() => approveLeave(request.id)}
                            style={{
                              background: 'linear-gradient(135deg, #10b981, #059669)',
                              border: 'none',
                              color: '#ffffff',
                              padding: '10px 20px',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '14px',
                              fontWeight: 600
                            }}
                          >
                            <CheckCircle size={18} />
                            {t('admin.approve')}
                          </button>
                          <button
                            onClick={() => rejectLeave(request.id)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.8)',
                              border: 'none',
                              color: '#ffffff',
                              padding: '10px 20px',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '14px',
                              fontWeight: 600
                            }}
                          >
                            <XCircle size={18} />
                            {t('admin.reject')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Onglet Notes de Frais */}
        {activeTab === 'frais' && !loading && (
          <div>
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '24px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{
                flex: 1,
                minWidth: '300px',
                position: 'relative'
              }}>
                <Search size={20} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }} />
                <input
                  type="text"
                  placeholder={t('admin.search_expenses')}
                  value={searchExpenses}
                  onChange={(e) => setSearchExpenses(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <select
                value={filterStatusExpenses}
                onChange={(e) => setFilterStatusExpenses(e.target.value)}
                style={{
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="all">{t('admin.all_statuses')}</option>
                <option value="pending">{t('admin.status_pending')}</option>
                <option value="approved">{t('admin.status_approved')}</option>
                <option value="rejected">{t('admin.status_rejected')}</option>
              </select>
            </div>

            {filteredExpenses.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '20px',
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                {t('admin.no_expenses')}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '20px'
              }}>
                {filteredExpenses.map(expense => (
                  <div
                    key={expense.id}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '20px',
                      padding: '24px'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: '16px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '12px'
                        }}>
                          <h3 style={{
                            fontSize: '18px',
                            fontWeight: 700,
                            margin: 0,
                            color: '#ffffff'
                          }}>
                            {expense.title}
                          </h3>
                          <span style={{
                            background: expense.status === 'pending' 
                              ? 'rgba(255, 193, 7, 0.2)' 
                              : expense.status === 'approved'
                              ? 'rgba(16, 185, 129, 0.2)'
                              : 'rgba(239, 68, 68, 0.2)',
                            color: expense.status === 'pending'
                              ? '#ffc107'
                              : expense.status === 'approved'
                              ? '#10b981'
                              : '#ef4444',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                          }}>
                            {expense.status === 'pending' ? t('admin.status_pending') : expense.status === 'approved' ? t('admin.status_approved') : t('admin.status_rejected')}
                          </span>
                        </div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '12px',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '14px'
                        }}>
                          <div><strong>{t('admin.employee')}</strong> {expense.employee_name}</div>
                          <div><strong>{t('admin.amount')}</strong> {expense.total_amount?.toLocaleString('fr-FR')} FCFA</div>
                          <div><strong>{t('admin.date')}</strong> {new Date(expense.expense_date).toLocaleDateString('fr-FR')}</div>
                          {expense.description && (
                            <div style={{ gridColumn: '1 / -1' }}>
                              <strong>{t('admin.description')}</strong> {expense.description}
                            </div>
                          )}
                          {expense.receipt_files && expense.receipt_files.length > 0 && (
                            <div style={{ gridColumn: '1 / -1' }}>
                              <strong>{t('admin.receipts')}</strong> {expense.receipt_files.length} {t('admin.files')}
                            </div>
                          )}
                        </div>
                      </div>
                      {expense.status === 'pending' && (
                        <div style={{
                          display: 'flex',
                          gap: '12px',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={() => approveExpense(expense.id)}
                            style={{
                              background: 'linear-gradient(135deg, #10b981, #059669)',
                              border: 'none',
                              color: '#ffffff',
                              padding: '10px 20px',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '14px',
                              fontWeight: 600
                            }}
                          >
                            <CheckCircle size={18} />
                            {t('admin.approve')}
                          </button>
                          <button
                            onClick={() => rejectExpense(expense.id)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.8)',
                              border: 'none',
                              color: '#ffffff',
                              padding: '10px 20px',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '14px',
                              fontWeight: 600
                            }}
                          >
                            <XCircle size={18} />
                            {t('admin.reject')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Onglet Gestion des Comptes */}
        {activeTab === 'comptes' && !loading && (
          <div>
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '24px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{
                flex: 1,
                minWidth: '300px',
                position: 'relative'
              }}>
                <Search size={20} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }} />
                <input
                  type="text"
                  placeholder={t('admin.search_users')}
                  value={searchUsers}
                  onChange={(e) => setSearchUsers(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {filteredUsers.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '20px',
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                {t('admin.no_users')}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: '20px'
              }}>
                {filteredUsers.map(user => (
                  <div
                    key={user.id}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '20px',
                      padding: '24px'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: 700,
                          marginBottom: '8px',
                          color: '#ffffff'
                        }}>
                          {user.first_name} {user.last_name}
                        </h3>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '14px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Mail size={16} />
                            {user.email}
                          </div>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: user.role === 'admin' 
                              ? 'rgba(124, 58, 237, 0.2)' 
                              : user.role === 'manager'
                              ? 'rgba(59, 130, 246, 0.2)'
                              : 'rgba(16, 185, 129, 0.2)',
                            color: user.role === 'admin'
                              ? '#a78bfa'
                              : user.role === 'manager'
                              ? '#60a5fa'
                              : '#34d399',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            width: 'fit-content'
                          }}>
                            {user.role === 'admin' ? 'Admin' : user.role === 'manager' ? 'Manager' : 'Employé'}
                          </div>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: user.is_active 
                              ? 'rgba(16, 185, 129, 0.2)' 
                              : 'rgba(239, 68, 68, 0.2)',
                            color: user.is_active ? '#10b981' : '#ef4444',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 600,
                            width: 'fit-content'
                          }}>
                            {user.is_active ? t('admin.active') : t('admin.inactive')}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}>
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowUserModal(true);
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: '#ffffff',
                          padding: '8px 16px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Eye size={16} />
                        {t('admin.view')}
                      </button>
                      <button
                        onClick={() => toggleUserStatus(user.id, user.is_active)}
                        style={{
                          background: user.is_active 
                            ? 'rgba(239, 68, 68, 0.8)' 
                            : 'rgba(16, 185, 129, 0.8)',
                          border: 'none',
                          color: '#ffffff',
                          padding: '8px 16px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {user.is_active ? <Ban size={16} /> : <UserCheck size={16} />}
                        {user.is_active ? t('admin.deactivate') : t('admin.activate')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Détails Utilisateur */}
      {showUserModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}
        onClick={() => setShowUserModal(false)}
        >
          <div
            style={{
              background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
              borderRadius: '24px',
              padding: '32px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#ffffff'
              }}>
                {t('admin.user_details')}
              </h2>
              <button
                onClick={() => setShowUserModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  color: '#ffffff',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '14px'
            }}>
              <div><strong>{t('admin.name')}</strong> {selectedUser.first_name} {selectedUser.last_name}</div>
              <div><strong>{t('admin.email')}</strong> {selectedUser.email}</div>
              <div><strong>{t('admin.role')}</strong> {selectedUser.role}</div>
              <div><strong>{t('admin.status')}</strong> {selectedUser.is_active ? t('admin.active') : t('admin.inactive')}</div>
              {selectedUser.department && <div><strong>{t('admin.department')}</strong> {selectedUser.department}</div>}
              <div><strong>{t('admin.created_at')}</strong> {new Date(selectedUser.created_at).toLocaleDateString('fr-FR')}</div>
              {selectedUser.last_login && (
                <div><strong>{t('admin.last_login')}</strong> {new Date(selectedUser.last_login).toLocaleDateString('fr-FR')}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;

